import type { ChartQueryPanel } from 'features/charts-v2/types/chart-query-panel'
import type { Condition } from 'features/charts-v2/types/yogiql'
import type {
  FeedbackService,
  PreviewsPage,
} from 'features/feedback/service/service'
import type { FeedbackResponse, SummaryResponse } from 'features/feedback/types'
import { withFeedbackReviewsOnly } from 'features/feedback/utils'

/** Which body the previews request sends: wait for the flag and catalog, the
 *  legacy panel, or the panel's filters as YogiQL conditions. */
export type FeedbackFiltersRequest =
  | { mode: 'pending' }
  | { mode: 'legacy' }
  | { mode: 'yogiql'; filters: Condition[] }

/** Whether the hub has `enhanced_dialect`, and whether the flags have loaded. */
export type YogiQLFlag = { enabled: boolean; settled: boolean }

export const YOGIQL_OFF: YogiQLFlag = { enabled: false, settled: true }

export const feedbackPostsQueryKey = (
  comparativeIndex: number,
  projectId: string,
  requestBody: ChartQueryPanel | FeedbackFiltersRequest | undefined,
  order: string,
  orderField: string,
  selectedTimeField: string,
): unknown[] => [
  `feedback-posts_` + comparativeIndex,
  projectId,
  requestBody,
  order,
  orderField,
  selectedTimeField,
]

/**
 * The previews body for a panel: pending until the flag settles, legacy when
 * it is off, else the panel's held YogiQL conditions (reviews-only
 * normalized, as the legacy body is). Pure.
 */
export function feedbackFiltersRequest(
  flag: YogiQLFlag,
  heldFilters: Condition[] | undefined,
  reviewsOnly: boolean,
): FeedbackFiltersRequest {
  if (!flag.settled) return { mode: 'pending' }
  if (!flag.enabled) return { mode: 'legacy' }
  if (!heldFilters) return { mode: 'pending' }
  return {
    mode: 'yogiql',
    filters: withFeedbackReviewsOnly(heldFilters, reviewsOnly),
  }
}

/** One previews page, from the YogiQL route when the filters encoded, else the legacy one. */
export function fetchPreviewsPage(
  feedbackService: FeedbackService,
  projectId: string,
  request: FeedbackFiltersRequest,
  { panel, ...page }: PreviewsPage & { panel: ChartQueryPanel },
): Promise<FeedbackResponse> {
  return request.mode === 'yogiql'
    ? feedbackService.getYogiQLPostPreviews(projectId, {
        filters: request.filters,
        ...page,
      })
    : feedbackService.getPostPreviews(projectId, { panel, ...page })
}

/** The summary panel, from the YogiQL route when the filters encoded, else the legacy one. */
export function fetchPostsSummary(
  feedbackService: FeedbackService,
  projectId: string,
  request: FeedbackFiltersRequest,
  panel: ChartQueryPanel,
): Promise<SummaryResponse> {
  return request.mode === 'yogiql'
    ? feedbackService.getYogiQLPostsSummary(projectId, {
        filters: request.filters,
      })
    : feedbackService.getPostsSummary({ proj_uuid: projectId }, { panel })
}
